import { Empty } from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import elementalSvg from "../assets/image/elemental 'e'.svg";
import OperationSwimlane from "../components/OperationSwimlane";
import Swimlane from "../components/Swimlane";
import { ProjectFocusButton } from "../components/SwimlaneFocusHelper";
import useHandleContextMenu from "../hooks/useHandleContextMenu";
import { getOperations } from "../services/api-server/operations";
import { getPhasesAndTasks } from "../services/api-server/phases";
import { getWells } from "../services/api-server/wells";
import { TOperationData } from "../types/operation";
import { TPhaseDataWithTasks } from "../types/phase";
import { TProjectData } from "../types/project";
import { TWellData } from "../types/well";
import { GetAntIcon } from "../utils/ant_icons";
import { HomeContext } from "./Home";

function MultiAssetKanbanBoard({ project }: { project: TProjectData }) {
	const [phaseList, setPhaseList] = useState<TPhaseDataWithTasks[]>([]);
	const [operationList, setOperationList] = useState<
		(TOperationData & TWellData)[]
	>([]);

	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const { currentTenantKey }: any = useContext(HomeContext);
	const { handleContextMenu } = useHandleContextMenu();

	// Refs for focusing on specific swimlanes
	const projectSwimlaneRef = useRef<HTMLDivElement>(null);
	const operationSwimlaneRefs = useRef<{
		[key: string]: HTMLDivElement | null;
	}>({});

	const fetchPhasesAndTasks = async () => {
		try {
			const { phase_data, task_data }: any = await getPhasesAndTasks(
				currentTenantKey,
				{
					project_id: project._id,
				}
			);
			phase_data.forEach((phase: TPhaseDataWithTasks) => {
				phase.tasks = task_data[phase._id] || [];
			});

			setPhaseList(phase_data as TPhaseDataWithTasks[]);
		} catch (error) {
			console.log(error);
		}
	};

	const fetchOperations = async () => {
		try {
			const operations = await getOperations(currentTenantKey, {
				project: project._id,
			});
			for (const operation of operations as TOperationData[] & TWellData[]) {
				const well: any = await getWells(currentTenantKey, {
					_id: operation.well_id,
				});
				operation.well_name = well[0]?.well_name;
			}
			setOperationList(operations as (TOperationData & TWellData)[]);
		} catch (error) {
			console.log(error);
		}
	};

	// Function to focus on a specific swimlane
	const focusOnSwimlane = (swimlaneType: string, id?: string) => {
		setTimeout(() => {
			let targetRef: HTMLDivElement | null = null;

			if (swimlaneType === "project") {
				targetRef = projectSwimlaneRef.current;
			} else if (swimlaneType === "operation" && id) {
				targetRef = operationSwimlaneRefs.current[id];
			}

			if (targetRef) {
				targetRef.scrollIntoView({
					behavior: "smooth",
					block: "start",
				});
				// Add CSS animation for focus effect
				targetRef.classList.add("swimlane-focus-highlight");
				setTimeout(() => {
					if (targetRef) {
						targetRef.classList.remove("swimlane-focus-highlight");
					}
				}, 2000);
			}
		}, 500); // Small delay to ensure components are rendered
	};

	useEffect(() => {
		fetchPhasesAndTasks();
		fetchOperations();
	}, []);

	// Effect to handle URL parameters for focusing on swimlanes
	useEffect(() => {
		const focusSwimlane = searchParams.get("focus");
		const focusId = searchParams.get("id");

		if (focusSwimlane && (phaseList.length > 0 || operationList.length > 0)) {
			focusOnSwimlane(focusSwimlane, focusId || undefined);
		}
	}, [searchParams, phaseList, operationList]);

	return (
		<div className="kanban-board-container">
			<div className="kanban-columns-container">
				{phaseList.length > 0 ? (
					<>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
							}}
						>
							<div style={{ display: "flex", gap: "30px" }}>
								{phaseList.map((phase) => (
									<div
										key={phase._id}
										style={{
											minWidth: "320px",
											maxWidth: "320px",
											height: "100%",
											display: "flex",
											flexDirection: "column",
											gap: "30px",
										}}
									>
										<div className="progress-bar-bg">
											<span style={{ zIndex: "1" }}>
												{Math.round(phase.progress) || 0}%
											</span>
											<div
												className="progress-bar"
												style={{
													width: `${Math.round(phase.progress) || 0}%`,
												}}
											></div>
										</div>
										<div className="phase-container">
											<div
												className={"project-status-container-header phase-item"}
												onClick={() => {
													navigate(`multi-asset/${phase._id}`, {
														replace: false,
													});
												}}
												onContextMenu={(e: any) => {
													const location = `${window.location.href}/multi-asset/${phase._id}`;
													handleContextMenu(e, { link: location });
												}}
											>
												{GetAntIcon("square-check")}
												<span>{phase.phase_name}</span>
											</div>
										</div>
									</div>
								))}
							</div>
							<div ref={projectSwimlaneRef}>
								<Swimlane
									label={"Project"}
									defaultOpen={true}
									phases={phaseList}
									extraComponent={
										<ProjectFocusButton projectId={project._id} />
									}
								/>
							</div>
							{operationList.map((operation) => {
								return (
									<div
										key={operation._id}
										ref={(el) => {
											operationSwimlaneRefs.current[operation._id] = el;
										}}
									>
										<OperationSwimlane
											project={project}
											operation={operation}
										/>
									</div>
								);
							})}
						</div>
					</>
				) : (
					<Empty
						className="no-data-empty"
						style={{ margin: "auto", fontSize: "20px" }}
						image={elementalSvg}
						description="No data"
					/>
				)}
			</div>
		</div>
	);
}

export default MultiAssetKanbanBoard;
